import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, of, tap, map } from 'rxjs';
import { environment } from '../environments/environment';


export interface Base {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export type CategoryType = 'food' | 'drinks' | 'other';

export interface Event extends Base {
  companyId: string;
  name: string;
  description: string;
  location: string;
  date?: string | null;
  isRestaurant: boolean;
  onlinePaymentEnabled: boolean;
}

export interface ProductCategory extends Base {
  eventId: string;
  companyId: string;
  name: string;
  parentId?: string;
  isParent: boolean;
  isSystemCategory: boolean;
  categoryType: CategoryType;
  parent?: ProductCategory;
}

export interface Product extends Base {
  eventId: string;
  companyId: string;
  name: string;
  description: string;
  price: number;
  isAvailable: boolean;
  // Remaining stock; missing/null = not tracked (unlimited)
  stock?: number | null;
  categoryId?: string;
  category?: ProductCategory;
  imageUrl?: string;
  imageBlobId?: string;
}

export type TableStatus = 'free' | 'occupied' | 'reserved';

export interface Table extends Base {
  eventId: string;
  companyId: string;
  number: string;
  status: TableStatus;
  qrCode: string;
}

export interface Item {
  productId: string;
  quantity: number;
  price: number;
  note?: string;
}

export type OrderStatus = 'new' | 'preparing' | 'ready' | 'delivered' | 'canceled';
export type OrderType = 'food' | 'drinks' | 'mixed';

export interface Order extends Base {
  eventId: string;
  companyId: string;
  tableId: string;
  status: OrderStatus;
  foodStatus: OrderStatus;
  drinksStatus: OrderStatus;
  totalPrice: number;
  tipAmount?: number;
  email?: string;
  orderType: OrderType;
  paymentId?: string;
  ordertable?: Table;
  items: Item[];
}

export interface CreateOrderRequest {
  items: Item[];
  tableId: string;
  email?: string;
}

export interface CheckoutSessionRequest {
  tableId: string;
  items: Item[];
  email?: string;
  tipPercent?: number;
}

export interface CheckoutSessionResponse {
  id: string;
  client_secret: string;
  url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      console.error(`${operation} failed:`, error);
      console.error('Error details:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        message: error.message,
        error: error.error  // Backend error details
      });
      return of(result as T);
    };
  }

  // PUBLIC ROUTES (keine Auth erforderlich)

  // GET /api/v1/public/events/:eventId → Event-Details abrufen
  getEvent(eventId: string): Observable<Event> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}`;
    console.log('API Call: GET', url);
    return this.http.get<Event>(url).pipe(
      tap(response => console.log('Event response:', response)),
      catchError(this.handleError<Event>('getEvent', {} as Event))
    );
  }

  // GET /api/v1/public/events/:eventId/products → Produkte öffentlich anzeigen
  getEventProducts(eventId: string): Observable<Product[]> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}/products`;
    console.log('API Call: GET', url);
    return this.http.get<Product[]>(url).pipe(
      tap(response => console.log('Event products response:', response)),
      catchError(this.handleError<Product[]>('getEventProducts', []))
    );
  }

  // GET /api/v1/public/events/:eventId/categories → Kategorien öffentlich anzeigen
  getEventCategories(eventId: string): Observable<ProductCategory[]> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}/categories`;
    console.log('API Call: GET', url);
    return this.http.get<ProductCategory[]>(url).pipe(
      tap(response => console.log('Event categories response:', response)),
      catchError(this.handleError<ProductCategory[]>('getEventCategories', []))
    );
  }

  // POST /api/v1/public/events/:eventId/orders → Öffentliche Bestellung erstellen
  createOrder(eventId: string, orderData: CreateOrderRequest): Observable<Order> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}/orders`;
    console.log('API Call: POST', url, orderData);
    return this.http.post<Order>(url, orderData).pipe(
      tap(response => console.log('Create order response:', response)),
      catchError(this.handleError<Order>('createOrder', {} as Order))
    );
  }

  // POST /api/v1/companies/:companyId/events/:eventId/orders → Bestellung als Mitarbeiter erstellen (ohne Stripe)
  createWaiterOrder(companyId: string, eventId: string, orderData: CreateOrderRequest, token: string): Observable<Order> {
    const url = `${this.baseUrl}/v1/companies/${companyId}/events/${eventId}/orders`;
    const headers = { 'Authorization': `Bearer ${token}` };
    console.log('API Call (Waiter): POST', url, orderData);
    return this.http.post<Order>(url, orderData, { headers }).pipe(
      tap(response => console.log('Create waiter order response:', response)),
      catchError(this.handleError<Order>('createWaiterOrder', {} as Order))
    );
  }

  // POST /api/v1/public/events/:eventId/checkout-sessions → Stripe Checkout Session erstellen
  createCheckoutSession(eventId: string, checkoutData: CheckoutSessionRequest): Observable<CheckoutSessionResponse> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}/checkout-sessions`;
    console.log('API Call: POST', url, checkoutData);
    return this.http.post<CheckoutSessionResponse>(url, checkoutData).pipe(
      tap(response => console.log('Create checkout session response:', response)),
      catchError((error: HttpErrorResponse) => {
        console.error('createCheckoutSession failed:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          message: error.message,
          error: error.error
        });
        throw error; // Re-throw the error instead of returning empty object
      })
    );
  }

  // GET /api/v1/public/events/:eventId/orders/:orderId → Öffentliche Bestellung abrufen
  getOrderStatus(eventId: string, orderId: string): Observable<Order> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}/orders/${orderId}`;
    console.log('API Call: GET', url);
    return this.http.get<Order>(url).pipe(
      tap(response => console.log('Order response:', response)),
      catchError(this.handleError<Order>('getOrderStatus', {} as Order))
    );
  }

  // GET /api/v1/public/events/:eventId/tables → Alle Tische für ein Event
  getAllTables(eventId: string): Observable<Table[]> {
    const url = `${this.baseUrl}/v1/public/events/${eventId}/tables`;
    console.log('API Call: GET', url);
    return this.http.get<Table[]>(url).pipe(
      tap(response => console.log('All tables response:', response)),
      catchError(this.handleError<Table[]>('getAllTables', []))
    );
  }

  // Einzelnen Tisch aus der Liste filtern (kein eigener Backend-Endpoint)
  getTable(eventId: string, tableId: string): Observable<Table | null> {
    return this.getAllTables(eventId).pipe(
      map(tables => tables.find(t => t.id === tableId) || null),
      tap(result => console.log('Filtered table:', result))
    );
  }

  getProductImageUrl(product: Product): string {
    if (product.imageUrl) {
      return product.imageUrl;
    }
    if (product.imageBlobId && product.eventId && product.companyId) {
      return `${this.baseUrl}/v1/public/events/${product.eventId}/products/${product.id}/images`;
    }

    return '';
  }

  // CONVENIENCE METHODS

  // Lade alle Daten für ein Event (Kategorien + Produkte)
  getEventMenu(eventId: string): Observable<{categories: ProductCategory[], products: Product[]}> {
    const categories$ = this.getEventCategories(eventId);
    const products$ = this.getEventProducts(eventId);

    return new Observable(observer => {
      let categoriesLoaded = false;
      let productsLoaded = false;
      let categories: ProductCategory[] = [];
      let products: Product[] = [];

      const checkComplete = () => {
        if (categoriesLoaded && productsLoaded) {
          observer.next({ categories, products });
          observer.complete();
        }
      };

      categories$.subscribe({
        next: (data) => {
          categories = data;
          categoriesLoaded = true;
          checkComplete();
        },
        error: (err) => observer.error(err)
      });

      products$.subscribe({
        next: (data) => {
          products = data;
          productsLoaded = true;
          checkComplete();
        },
        error: (err) => observer.error(err)
      });
    });
  }
}
